import {
  Button,
  Dialog,
  Icon,
  BadgeGreen,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "@sellar-io/components";
import dayjs from "dayjs";
import {
  createContext,
  ReactNode,
  useContext,
  useId,
  useMemo,
  useState,
} from "react";
import { useDiscoveryDistributionSupplierList } from "../../../../../hooks/api/v2/discovery/useDiscoveryDistributionSupplierList";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { pluralize } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { Alert } from "../../../../../components/alert/Alert";
import {
  SupplierRelationshipFulfillmentType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { useBasketContext } from "../../../../../hooks";
import { useDirectDeliveryEstimate } from "../../../../../hooks/useDeliveryEstimates/useDirectDeliveryEstimate";
import { useDistributionDeliveryEstimate } from "~/hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";

type OptionId = SupplierRelationshipFulfillmentType;

type StorefrontBuyerChoiceDeliveryOptionDialogProps = {
  open: boolean;
  initialOptionId: OptionId | null;
  supplier: SupplierGetResponseType;
  sdDeliveryArea: SupplierStorefrontV2GetResponseType["sdDeliveryArea"];
  directDeliveryArea: SupplierStorefrontV2GetResponseType["DeliveryArea"];
  onDismiss: () => void;
  onConfirm: (optionId: OptionId) => void;
};

export const StorefrontBuyerChoiceDeliveryOptionDialog = ({
  initialOptionId,
  open,
  supplier,
  directDeliveryArea,
  onDismiss,
  onConfirm,
}: StorefrontBuyerChoiceDeliveryOptionDialogProps) => {
  const { nextDistributionDeliveryDate, nextDirectDeliveryDate } =
    useDeliveryEstimates({
      supplier,
      directDeliveryArea,
    });
  const groupName = useId();
  const supplierName = supplier.Business.name;
  const [_selectedOptionId, setSelectedOptionId] = useState<OptionId | null>(
    null,
  );
  const selectedOptionId = _selectedOptionId ?? initialOptionId;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirm = () => {
    setErrorMessage("");
    if (!selectedOptionId) {
      setErrorMessage(
        "Select your preferred direct delivery option to continue",
      );
      return;
    }
    onConfirm(selectedOptionId);
  };

  const handleDismiss = () => {
    setSelectedOptionId(null);
    onDismiss();
  };

  const supplierBusinessName = supplier.Business.name;

  const hasSelectedOption = Boolean(selectedOptionId);

  const isDeliveryDateEqual =
    nextDistributionDeliveryDate && nextDirectDeliveryDate
      ? dayjs(nextDistributionDeliveryDate)
          .startOf("day")
          .isSame(dayjs(nextDirectDeliveryDate).startOf("day"))
      : false;

  const sortedOptionAndDates: Array<{ optionId: OptionId; date: Date | null }> =
    [
      ...(nextDistributionDeliveryDate
        ? [
            {
              optionId: "sellarDistribution" as const,
              date: dayjs(nextDistributionDeliveryDate).startOf("day").toDate(),
            },
          ]
        : []),
      ...(nextDirectDeliveryDate
        ? [
            {
              optionId: "direct" as const,
              date: dayjs(nextDirectDeliveryDate).startOf("day").toDate(),
            },
          ]
        : []),
    ].toSorted((a, b) => a.date.getTime() - b.date.getTime());

  const earliestOption = !isDeliveryDateEqual
    ? sortedOptionAndDates[0]?.optionId
    : undefined;

  return (
    <Dialog active={open} onDismiss={handleDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={handleDismiss}
        title="Choose your preferred direct delivery option"
      />
      <DialogContent className="flex flex-col gap-2">
        {errorMessage && (
          <Alert type="error" className="w-full" message={errorMessage} />
        )}
        <DeliveryOptionsGroup
          onChange={setSelectedOptionId}
          optionId={selectedOptionId}
          name={`delivery-options-${groupName}`}
        >
          <div className="flex flex-col gap-4">
            <DeliveryOptionsGroupOption optionId="sellarDistribution">
              <SellarDistributionDeliveryOption
                optionId="sellar-distribution"
                selected={
                  hasSelectedOption && selectedOptionId === "sellarDistribution"
                }
                deliveredAt={nextDistributionDeliveryDate}
                arrivesFirst={earliestOption === "sellarDistribution"}
                supplierBusinessName={supplierBusinessName}
              />
            </DeliveryOptionsGroupOption>
            <DeliveryOptionsGroupOption optionId="direct">
              <DirectDeliveryOption
                optionId="direct"
                selected={hasSelectedOption && selectedOptionId === "direct"}
                deliveredAt={nextDirectDeliveryDate}
                supplierName={supplierName}
                arrivesFirst={earliestOption === "direct"}
              />
            </DeliveryOptionsGroupOption>
          </div>
        </DeliveryOptionsGroup>
      </DialogContent>
      <DialogCTA>
        <Button onClick={handleConfirm} disabled={!selectedOptionId}>
          Continue
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

type DeliveryOptionsContextValue = {
  optionId: OptionId | null;
  name: string;
  onSelect: (optionId: OptionId) => void;
};

const DeliveryOptionsContext =
  createContext<DeliveryOptionsContextValue | null>(null);

type DeliveryOptionsGroupRootProps = {
  optionId: OptionId | null;
  name: string;
  children: ReactNode;
  onChange: (optionId: OptionId) => void;
};

const DeliveryOptionsGroupRoot = ({
  optionId,
  name,
  children,
  onChange,
}: DeliveryOptionsGroupRootProps) => {
  const value = useMemo(
    () => ({
      optionId,
      name,
      onSelect: onChange,
    }),
    [optionId, name, onChange],
  );

  return (
    <DeliveryOptionsContext.Provider value={value}>
      <div role="radiogroup">{children}</div>
    </DeliveryOptionsContext.Provider>
  );
};

type DeliveryOptionProps = {
  optionId: OptionId;
  disabled?: boolean;
  children: ReactNode;
};

const DeliveryOption = ({
  optionId,
  disabled,
  children,
}: DeliveryOptionProps) => {
  const deliveryOptionContext = useContext(DeliveryOptionsContext);

  if (!deliveryOptionContext) {
    throw new Error("DeliveryOption must be used within DeliveryOptionsGroup");
  }

  const selected = deliveryOptionContext.optionId === optionId;

  const handleSelect = () => {
    if (disabled) {
      return;
    }

    deliveryOptionContext.onSelect(optionId);
  };

  return (
    <div
      role="radio"
      className="cursor-pointer"
      aria-checked={selected}
      onClick={handleSelect}
    >
      <input
        type="radio"
        name={deliveryOptionContext.name}
        checked={selected}
        onChange={() => deliveryOptionContext.onSelect(optionId)}
        hidden
      />

      {children}
    </div>
  );
};

const DeliveryOptionsGroup = DeliveryOptionsGroupRoot;
const DeliveryOptionsGroupOption = DeliveryOption;

const SellarDistributionDeliveryOption = ({
  deliveredAt,
  arrivesFirst,
  selected,
  supplierBusinessName,
}: {
  deliveredAt: Date | null;
  optionId: string;
  selected: boolean;
  arrivesFirst: boolean;
  supplierBusinessName: string;
}) => {
  const { business } = useSellarCurrentBusiness();

  const retailerBusinessPostcode = business?.Retailer
    ? business?.DeliveryAddress.postalCode
    : null;

  const discoveryDistributionSupplierListQuery =
    useDiscoveryDistributionSupplierList(
      {
        query: retailerBusinessPostcode
          ? [
              {
                type: "retailerPostalCode",
                modifier: "EQUALS",
                value: retailerBusinessPostcode,
              } as const,
            ]
          : [],
      },
      {
        enabled: Boolean(retailerBusinessPostcode),
      },
    );

  const distributionSuppliers =
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? [];

  const supplierCountText = [
    supplierBusinessName,
    `${distributionSuppliers.length - 1}`,
  ].join(" +");

  return (
    <div
      className={twMerge(
        "flex flex-col rounded-lg border border-zinc-300",
        selected && "border-zinc-800 bg-zinc-200",
      )}
    >
      <div className="flex gap-4 p-4">
        <input
          type="radio"
          checked={selected}
          readOnly
          className="accent-zinc-700 outline-none"
        />
        <div className="flex w-full flex-col gap-1">
          <div className="flex flex-1 justify-between">
            <span className="text-lg font-semibold text-zinc-800">
              Delivered by Sellar
            </span>
            <BadgeGreen>Free</BadgeGreen>
          </div>
          <span className="text-base font-normal text-zinc-600">
            Orders delivered together by our expert dray team
          </span>
          <div className="flex items-center gap-2 text-base font-normal">
            <div>
              Delivering{" "}
              <span className="underline">
                {deliveredAt ? dayjs(deliveredAt).format("dddd D MMM") : "soon"}
              </span>
            </div>
            {arrivesFirst && <CustomBadge>Arrives soonest</CustomBadge>}
          </div>
        </div>
      </div>
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 rounded-b-lg border-t border-t-[#E8D3F4] bg-[#E8D3F426] p-2 text-xs font-medium sm:flex-row",
          selected && "border-t-[#D1A7E9] bg-[#E8D3F499]",
        )}
      >
        <div className="flex -space-x-1">
          {Array(3)
            .fill(true)
            .map((_item, index) => (
              <div
                key={index}
                className={twMerge(
                  "flex h-6 w-6 items-center justify-center rounded-full",
                  index === 0 && "bg-red-100",
                  index === 1 && "bg-blue-100",
                  index === 2 && "bg-emerald-100",
                )}
              >
                <Icon
                  icon="KEG"
                  size={0.7}
                  className={twMerge(
                    index === 0 && "text-red-800",
                    index === 1 && "text-blue-800",
                    index === 2 && "text-emerald-800",
                  )}
                />
              </div>
            ))}
        </div>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-0">
          <p className="flex gap-1">
            {supplierCountText}{" "}
            {pluralize("supplier", distributionSuppliers.length)}{" "}
            <span className="text-[#8C23C7]">delivered together.</span>{" "}
          </p>
        </div>
      </div>
    </div>
  );
};

const DirectDeliveryOption = ({
  deliveredAt,
  supplierName,
  arrivesFirst,
  selected,
}: {
  deliveredAt: Date | null;
  optionId: string;
  selected: boolean;
  supplierName: string;
  arrivesFirst: boolean;
}) => {
  return (
    <div
      className={twMerge(
        "flex flex-col rounded-lg border border-zinc-300",
        selected && "border-zinc-800 bg-zinc-200",
      )}
    >
      <div className="flex gap-4 p-4">
        <input
          type="radio"
          checked={selected}
          readOnly
          className="accent-zinc-700 outline-none"
        />
        <div className="flex w-full flex-col gap-1">
          <div className="flex flex-1 justify-between">
            <span className="text-lg font-semibold text-zinc-800">
              Delivered by {supplierName}
            </span>
            <BadgeGreen>Free</BadgeGreen>
          </div>
          <span className="text-base font-normal text-zinc-600">
            Delivered direct by the supplier
          </span>
          <div className="flex items-center gap-2 text-base font-normal">
            <div>
              Delivering{" "}
              <span className="underline">
                {deliveredAt ? dayjs(deliveredAt).format("dddd D MMM") : "soon"}
              </span>
            </div>
            {arrivesFirst && <CustomBadge>Arrives soonest</CustomBadge>}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomBadge = ({ children }: { children: ReactNode }) => {
  return (
    <div className="truncate rounded-lg bg-zinc-100 px-2 py-1 text-xs font-semibold text-zinc-800">
      {children}
    </div>
  );
};

const useDeliveryEstimates = ({
  supplier,
  directDeliveryArea,
}: {
  supplier: SupplierGetResponseType;
  directDeliveryArea: SupplierStorefrontV2GetResponseType["DeliveryArea"];
}) => {
  const { business } = useSellarCurrentBusiness();

  const [{ order }] = useBasketContext();

  const buyerPostcode =
    business?.type === "retailer" ? business.DeliveryAddress.postalCode : "";
  const supplierId = supplier.id;
  const productIds = order?.OrderItems?.map((item) => item.ProductId) ?? [];

  const { nextDeliveryDate: nextDistributionDeliveryDate } =
    useDistributionDeliveryEstimate({
      supplierId,
      buyerPostcode,
      productIds,
    });

  const preorderAvailableFromDates =
    order?.OrderItems?.map((item) => ({
      availableFrom: item.Product.availableFrom,
    })) ?? [];

  const directDeliveryEstimates = useDirectDeliveryEstimate({
    directDeliveryArea,
    supplier,
    preorderAvailableFromDates,
  });

  return {
    nextDistributionDeliveryDate,
    nextDirectDeliveryDate:
      directDeliveryEstimates.nextDirectDeliveryDate?.deliveryDate ?? null,
  };
};
